import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkle, MusicNotes, ArrowRight } from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Spotify-Wrapped-style summary of the user's last 7 days of worship
// listening. Tappable: links to /my-mix where the personalized
// auto-playlist plays. Empty state nudges new users to start listening.
export default function WeekInWorshipCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user === false) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    api
      .get("/my-week-in-worship")
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Hide entirely for logged-out users — they have a separate sign-in CTA.
  if (!user || user === false || loading) return null;

  const hasData = data?.has_data;

  return (
    <Link
      to="/my-mix"
      data-testid="week-in-worship-card"
      className="block group"
    >
      <div className="relative overflow-hidden rounded-2xl border border-amber-200/40 bg-gradient-to-br from-amber-100 via-orange-200 to-rose-300 p-5 shadow-sm transition-transform group-hover:scale-[1.01] group-active:scale-[0.99]">
        {/* Decorative grain blob */}
        <div className="pointer-events-none absolute -top-10 -right-10 h-40 w-40 rounded-full bg-white/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-12 -left-8 h-36 w-36 rounded-full bg-rose-400/30 blur-3xl" />

        <div className="relative flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-rose-900/80">
              <Sparkle weight="fill" size={12} />
              Your Week in Worship
            </div>
            {hasData ? (
              <div className="mt-2 space-y-1">
                <div
                  data-testid="week-top-artist"
                  className="text-lg sm:text-xl font-bold text-stone-900 truncate"
                >
                  {data.top_artist || "—"}
                </div>
                {data.top_track && (
                  <div className="text-xs text-stone-800/80 truncate">
                    Top song · {data.top_track.title}
                  </div>
                )}
                <div className="flex items-center gap-3 pt-1 text-[11px] text-stone-800/80">
                  <span
                    data-testid="week-total-plays"
                    className="inline-flex items-center gap-1"
                  >
                    <MusicNotes weight="fill" size={11} />
                    {data.total_plays} plays
                  </span>
                  <span>·</span>
                  <span>~{data.minutes} min</span>
                </div>
              </div>
            ) : (
              <div className="mt-2 space-y-1">
                <div className="text-base sm:text-lg font-bold text-stone-900">
                  Start your week in worship
                </div>
                <p className="text-[11px] text-stone-800/80 leading-snug">
                  Play a song, a sermon, or a Sunday service — we'll spin you a
                  personalized mix next week.
                </p>
              </div>
            )}
          </div>
          <div className="shrink-0 mt-1 inline-flex h-9 w-9 items-center justify-center rounded-full bg-stone-900/90 text-amber-50 transition-transform group-hover:translate-x-0.5">
            <ArrowRight size={16} weight="bold" />
          </div>
        </div>

        <div className="relative mt-3 inline-flex items-center gap-1.5 rounded-full bg-stone-900/90 px-3 py-1.5 text-[11px] font-semibold text-amber-50">
          <MusicNotes size={12} weight="fill" />
          Open My Worship Mix
        </div>
      </div>
    </Link>
  );
}
